import type { Brief } from './brief.js';

/**
 * Picture tiles — how a five-to-seven-year-old writes a brief.
 *
 * The pedagogy is different at this age. An older child learns "a vague brief
 * makes a boring game, so specify better". A six-year-old cannot yet type that,
 * and would just be stuck. What she can learn is the thing underneath it:
 * *what I choose changes what I get*. So the tiles are combinatorially
 * expressive — jungle+monkey+swing+bananas produces a genuinely different game
 * from space+robot+float+stars — and every combination yields a brief specific
 * enough to build something good from.
 *
 * That is why each tile carries a `phrase` as well as a `label`. The label is
 * the one or two words she reads; the phrase is the vivid, specific sentence
 * that goes to the model. She gets the benefit of a well-specified brief before
 * she can write one, and the habit of specifying comes later, on the same form.
 *
 * Emoji rather than images on purpose: no external requests, nothing to load,
 * works offline, and identical inside the generated game's strict CSP.
 */

export type Tile = {
  id: string;
  emoji: string;
  /** What the child reads. One or two words, no reading age above six. */
  label: string;
  /** What the model receives. Specific and vivid, in her voice. */
  phrase: string;
};

export const WORLD_TILES: Tile[] = [
  { id: 'jungle', emoji: '🌴', label: 'Jungle', phrase: 'a steamy green jungle with tall trees and hanging vines' },
  { id: 'space', emoji: '🪐', label: 'Space', phrase: 'outer space, floating between bright stars and little planets' },
  { id: 'sea', emoji: '🌊', label: 'Under the sea', phrase: 'deep under the sea, with coral and bubbles everywhere' },
  { id: 'castle', emoji: '🏰', label: 'Castle', phrase: 'a big stone castle with towers and torches on the walls' },
  { id: 'snow', emoji: '❄️', label: 'Snow', phrase: 'a snowy mountain with ice and drifting snowflakes' },
  { id: 'kitchen', emoji: '🥞', label: 'Kitchen', phrase: 'a giant kitchen where everything is enormous, on top of the counter' },
  { id: 'desert', emoji: '🏜️', label: 'Desert', phrase: 'a hot sandy desert with cactuses and dunes' },
  { id: 'garden', emoji: '🌻', label: 'Garden', phrase: 'a sunny garden with huge flowers and buzzing bees' },
];

export const CHARACTER_TILES: Tile[] = [
  { id: 'cat', emoji: '🐱', label: 'Cat', phrase: 'a small quick cat' },
  { id: 'robot', emoji: '🤖', label: 'Robot', phrase: 'a friendly little robot with blinking lights' },
  { id: 'dragon', emoji: '🐲', label: 'Dragon', phrase: 'a tiny baby dragon who is learning to fly' },
  { id: 'penguin', emoji: '🐧', label: 'Penguin', phrase: 'a round penguin who slides about' },
  { id: 'monkey', emoji: '🐵', label: 'Monkey', phrase: 'a cheeky monkey with long arms' },
  { id: 'knight', emoji: '🛡️', label: 'Knight', phrase: 'a brave little knight in shiny armour' },
  { id: 'fish', emoji: '🐠', label: 'Fish', phrase: 'a bright stripy fish' },
  { id: 'bug', emoji: '🐞', label: 'Ladybird', phrase: 'a spotty ladybird who can scuttle fast' },
];

export const ACTION_TILES: Tile[] = [
  { id: 'jump', emoji: '⬆️', label: 'Jump', phrase: 'jump around and land on things' },
  { id: 'fly', emoji: '🪽', label: 'Fly', phrase: 'fly about, flapping to stay up in the air' },
  { id: 'swim', emoji: '🫧', label: 'Swim', phrase: 'swim around in every direction' },
  { id: 'run', emoji: '💨', label: 'Run', phrase: 'run about really fast' },
  { id: 'collect', emoji: '🧺', label: 'Collect', phrase: 'gather up everything you can find' },
  { id: 'dodge', emoji: '🌀', label: 'Dodge', phrase: 'dodge out of the way of things moving about' },
];

export const GOAL_TILES: Tile[] = [
  { id: 'all', emoji: '⭐', label: 'Get them all', phrase: 'you win by collecting every single one of them' },
  { id: 'top', emoji: '🗻', label: 'Reach the top', phrase: 'you win by getting all the way to the top' },
  { id: 'treasure', emoji: '💎', label: 'Find treasure', phrase: 'you win by finding the hidden treasure' },
  { id: 'friends', emoji: '💛', label: 'Help friends', phrase: 'you win by finding all your friends and helping them' },
];

export const TILE_GROUPS = {
  world: { question: 'Where does it happen?', tiles: WORLD_TILES },
  character: { question: 'Who are you?', tiles: CHARACTER_TILES },
  action: { question: 'What do you do?', tiles: ACTION_TILES },
  goal: { question: 'How do you win?', tiles: GOAL_TILES },
} as const;

export type TileChoice = {
  world?: string;
  character?: string;
  action?: string;
  goal?: string;
  /** Anything she added in her own words, by voice or by tapping letters. */
  extra?: string;
};

const find = (tiles: Tile[], id: string | undefined): Tile | undefined =>
  id === undefined ? undefined : tiles.find((t) => t.id === id);

/**
 * Turn tile choices into the same Brief an older child types.
 *
 * One brief shape for every age, so the moderation, coaching, building and
 * scanning paths downstream are identical — there is no second, less-examined
 * code path for the youngest child.
 */
export function tilesToBrief(choice: TileChoice): Brief | null {
  const world = find(WORLD_TILES, choice.world);
  const character = find(CHARACTER_TILES, choice.character);
  const action = find(ACTION_TILES, choice.action);
  const goal = find(GOAL_TILES, choice.goal);

  // The three that decide what the game actually is.
  if (!world || !character || !action) return null;

  const extra = (choice.extra ?? '').trim().slice(0, 200);

  return {
    world: extra ? `${world.phrase}. ${extra}` : world.phrase,
    character: character.phrase,
    action: action.phrase,
    winCondition: goal?.phrase ?? '',
    loseCondition: '',
  };
}

/** Whether enough is chosen to build. Drives the button state. */
export const isChoiceComplete = (choice: TileChoice): boolean =>
  tilesToBrief(choice) !== null;
